import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { contractsApi, nodesApi, documentsApi } from '../lib/api';
import { formatDate, formatDateTime, STATUS_LABELS, getStatusColor, ROLE_LABELS, formatFileSize } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    ArrowLeft,
    CheckCircle,
    Play,
    ArrowClockwise,
    Upload,
    Download,
    Check,
    X,
    Clock,
    Warning,
    Folder,
    ListChecks,
    TextAlignLeft,
    Database
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import StepFormRenderer from '../components/StepFormRenderer';
import DocumentManager from '../components/DocumentManager';
import ContractBaseData from '../components/ContractBaseData';
import SopralluogoStep from '../components/SopralluogoStep';
import ProgettazioneStep from '../components/ProgettazioneStep';

export default function ContractDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [contract, setContract] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedNodeDetails, setSelectedNodeDetails] = useState(null);
    const [loadingNodeDetails, setLoadingNodeDetails] = useState(false);
    const [savingFormData, setSavingFormData] = useState(false);

    const loadContract = useCallback(async () => {
        try {
            const [contractRes, timelineRes] = await Promise.all([
                contractsApi.getOne(id),
                contractsApi.getTimeline(id)
            ]);
            setContract(contractRes.data);
            setTimeline(timelineRes.data);
            // Set initial selected node only on first load
            setSelectedNode(prev => {
                if (prev) return prev; // Keep existing selection
                if (contractRes.data.nodes?.length > 0) {
                    const activeNode = contractRes.data.nodes.find(n => n.status === 'in_corso');
                    return activeNode || contractRes.data.nodes[0];
                }
                return prev;
            });
        } catch (error) {
            console.error('Error loading contract:', error);
            toast.error('Errore nel caricamento del contratto');
        } finally {
            setLoading(false);
        }
    }, [id]);

    // Load node details with form_sections when node is selected
    const loadNodeDetails = useCallback(async (node) => {
        if (!node?.id) return;
        
        setLoadingNodeDetails(true);
        try {
            const response = await nodesApi.getOne(node.id);
            setSelectedNodeDetails(response.data);
        } catch (error) {
            console.error('Error loading node details:', error);
            // Use basic node data if API fails
            setSelectedNodeDetails(node);
        } finally {
            setLoadingNodeDetails(false);
        }
    }, []);

    useEffect(() => {
        loadContract();
    }, [loadContract]);

    useEffect(() => {
        if (selectedNode) {
            loadNodeDetails(selectedNode);
        }
    }, [selectedNode, loadNodeDetails]);

    // Save form data
    const handleSaveFormData = async (formData) => {
        if (!selectedNode?.id) return;
        
        setSavingFormData(true);
        try {
            await nodesApi.updateFormData(selectedNode.id, formData);
            toast.success('Dati salvati con successo');
            // Refresh node details
            loadNodeDetails(selectedNode);
        } catch (error) {
            console.error('Error saving form data:', error);
            toast.error('Errore nel salvataggio dei dati');
        } finally {
            setSavingFormData(false);
        }
    };

    const handleNodeAction = async (action) => {
        if (!selectedNode) return;
        
        try {
            switch (action) {
                case 'start':
                    await nodesApi.start(selectedNode.id);
                    toast.success('Step avviato');
                    break;
                case 'complete':
                    await nodesApi.complete(selectedNode.id);
                    toast.success('Step completato');
                    break;
                case 'approve':
                    await nodesApi.approve(selectedNode.id);
                    toast.success('Step approvato');
                    break;
                case 'reopen':
                    await nodesApi.reopen(selectedNode.id);
                    toast.success('Step riaperto');
                    break;
                default:
                    break;
            }
            loadContract();
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.detail || 'Errore nell\'operazione');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!contract) {
        return (
            <div className="text-center py-12">
                <p className="text-muted-foreground">Contratto non trovato</p>
                <Button variant="link" onClick={() => navigate('/contratti')}>
                    Torna ai contratti
                </Button>
            </div>
        );
    }

    const completedSteps = contract.nodes?.filter(n => n.status === 'completato').length || 0;
    const totalSteps = contract.nodes?.length || 0;
    const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

    // Build customer display name
    const customerName = contract.customer?.ragione_sociale 
        ? contract.customer.ragione_sociale 
        : `${contract.customer?.nome || ''} ${contract.customer?.cognome || ''}`.trim();
    
    // Site address - Build from extracted sito fields, immobile data, or legacy fields
    const buildSiteAddress = () => {
        // PRIORITY 1: Extracted site data (indirizzo_sito, citta_sito, etc.)
        if (contract.indirizzo_sito || contract.citta_sito) {
            const parts = [];
            if (contract.indirizzo_sito) {
                parts.push(contract.indirizzo_sito);
            }
            if (contract.cap_sito || contract.citta_sito) {
                parts.push([contract.cap_sito, contract.citta_sito].filter(Boolean).join(' '));
            }
            if (contract.provincia_sito) {
                parts.push(`(${contract.provincia_sito})`);
            }
            const fullAddress = parts.join(' - ');
            if (fullAddress) return fullAddress;
        }
        
        // PRIORITY 2: Immobile data (manual entry)
        if (contract.immobile) {
            const { indirizzo, civico, cap, comune, provincia } = contract.immobile;
            const parts = [];
            if (indirizzo) {
                parts.push(civico ? `${indirizzo}, ${civico}` : indirizzo);
            }
            if (cap || comune) {
                parts.push([cap, comune].filter(Boolean).join(' '));
            }
            if (provincia) {
                parts.push(`(${provincia})`);
            }
            const fullAddress = parts.join(' - ');
            if (fullAddress) return fullAddress;
        }
        
        // PRIORITY 3: Legacy fields
        return contract.indirizzo_impianto || contract.site_address || '';
    };
    const siteAddress = buildSiteAddress();

    return (
        <div className="space-y-6 animate-fade-in" data-testid="contract-detail-page">
            {/* Header - Improved with customer and site info */}
            <div className="bg-gradient-to-r from-primary/5 to-primary/10 -mx-6 -mt-6 px-6 pt-6 pb-5 border-b">
                <Link
                    to="/contratti"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Torna ai contratti
                </Link>
                
                <div className="flex items-start justify-between">
                    <div className="space-y-3">
                        {/* Contract Number and Status */}
                        <div className="flex items-center gap-3">
                            <h1 className="font-heading text-2xl font-bold">
                                {contract.numero_contratto}
                            </h1>
                            <Badge className={`${getStatusColor(contract.status)} text-sm px-3 py-1`}>
                                {STATUS_LABELS[contract.status]}
                            </Badge>
                        </div>
                        
                        {/* Customer Info */}
                        <div className="flex items-center gap-4 text-base">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <span className="text-primary font-semibold text-sm">
                                        {(customerName || '?').charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <p className="font-medium" data-testid="customer-name">
                                        {customerName || <span className="text-muted-foreground italic">Cliente non specificato</span>}
                                    </p>
                                    {contract.customer?.email && (
                                        <p className="text-xs text-muted-foreground">{contract.customer.email}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Site Installation Address */}
                        {siteAddress ? (
                            <div className="flex items-start gap-2 text-sm bg-white/60 rounded-md px-3 py-2 max-w-md">
                                <svg className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <div className="flex flex-col">
                                    <span className="text-xs text-muted-foreground/70 font-medium">Sito installazione</span>
                                    <span className="text-muted-foreground" data-testid="site-address">{siteAddress}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-md px-3 py-2">
                                <Warning className="w-4 h-4" />
                                <span>Indirizzo sito installazione non specificato</span>
                            </div>
                        )}
                    </div>
                    
                    {/* Right side - Product and Date */}
                    <div className="text-right space-y-2">
                        <Badge variant="outline" className="text-base px-4 py-1.5 font-medium">
                            {contract.product_name || contract.codice_prodotto || 'N/D'}
                        </Badge>
                        <p className="text-sm text-muted-foreground">
                            Data: <span className="font-medium text-foreground">{formatDate(contract.data_contratto)}</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Progress Overview */}
            <Card>
                <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium">Avanzamento Workflow</span>
                        <span className="text-sm text-muted-foreground">
                            {completedSteps} di {totalSteps} step completati
                        </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                </CardContent>
            </Card>

            {/* Main Tabs */}
            <Tabs defaultValue="workflow" className="space-y-6">
                <TabsList className="w-full justify-start bg-muted/50 p-1">
                    <TabsTrigger value="workflow" className="gap-2" data-testid="tab-workflow">
                        <ListChecks className="w-4 h-4" />
                        Workflow
                    </TabsTrigger>
                    <TabsTrigger value="dati-base" className="gap-2" data-testid="tab-dati-base">
                        <Database className="w-4 h-4" />
                        Dati Base
                    </TabsTrigger>
                </TabsList>

                {/* Workflow Tab */}
                <TabsContent value="workflow" className="mt-0">
                    {/* Main Content */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Steps List */}
                <Card className="lg:col-span-1">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg font-heading">Fasi del Workflow</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                            {contract.nodes?.map((node, index) => (
                                <button
                                    key={node.id}
                                    onClick={() => setSelectedNode(node)}
                                    data-testid={`node-${node.code}`}
                                    className={`w-full p-4 text-left hover:bg-muted/30 transition-colors ${
                                        selectedNode?.id === node.id ? 'bg-muted/50' : ''
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            node.status === 'completato' ? 'bg-green-100 text-green-600' :
                                            node.status === 'in_corso' ? 'bg-blue-100 text-blue-600' :
                                            node.status === 'bloccato' ? 'bg-red-100 text-red-600' :
                                            'bg-muted text-muted-foreground'
                                        }`}>
                                            {node.status === 'completato' ? (
                                                <CheckCircle weight="fill" className="w-4 h-4" />
                                            ) : (
                                                <span className="text-xs font-medium">{index + 1}</span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{node.name}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Badge className={`text-xs ${getStatusColor(node.status)}`}>
                                                    {STATUS_LABELS[node.status]}
                                                </Badge>
                                                {node.is_blocking && (
                                                    <Badge variant="outline" className="text-xs">
                                                        Bloccante
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Step Detail */}
                <Card className="lg:col-span-2">
                    {selectedNode ? (
                        <>
                            <CardHeader className="border-b border-border">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="font-heading text-xl">{selectedNode.name}</CardTitle>
                                        <CardDescription className="mt-1">
                                            {selectedNode.description || 'Nessuna descrizione'}
                                        </CardDescription>
                                    </div>
                                    <Badge className={getStatusColor(selectedNode.status)}>
                                        {STATUS_LABELS[selectedNode.status]}
                                    </Badge>
                                </div>
                                <div className="flex gap-2 mt-4">
                                    {selectedNode.status === 'non_iniziato' && (
                                        <Button 
                                            onClick={() => handleNodeAction('start')}
                                            data-testid="start-node-button"
                                        >
                                            <Play className="w-4 h-4 mr-2" />
                                            Avvia Step
                                        </Button>
                                    )}
                                    {selectedNode.status === 'in_corso' && (
                                        <Button 
                                            onClick={() => handleNodeAction('complete')}
                                            data-testid="complete-node-button"
                                        >
                                            <CheckCircle className="w-4 h-4 mr-2" />
                                            Completa
                                        </Button>
                                    )}
                                    {selectedNode.approval_required && selectedNode.approval_status !== 'approvato' && (
                                        <Button 
                                            variant="outline"
                                            onClick={() => handleNodeAction('approve')}
                                            data-testid="approve-node-button"
                                        >
                                            <Check className="w-4 h-4 mr-2" />
                                            Approva
                                        </Button>
                                    )}
                                    {selectedNode.status === 'completato' && (
                                        <Button 
                                            variant="outline"
                                            onClick={() => handleNodeAction('reopen')}
                                            data-testid="reopen-node-button"
                                        >
                                            <ArrowClockwise className="w-4 h-4 mr-2" />
                                            Riapri
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Tabs defaultValue={(selectedNodeDetails?.form_sections?.length > 0 || selectedNodeDetails?.code === 'SOPRALLUOGO' || selectedNodeDetails?.code === 'PROGETTAZIONE_FATTIBILITA') ? "form" : "documents"}>
                                    <TabsList className="w-full justify-start rounded-none border-b border-border px-4 h-12">
                                        {(selectedNodeDetails?.form_sections?.length > 0 || selectedNodeDetails?.code === 'SOPRALLUOGO' || selectedNodeDetails?.code === 'PROGETTAZIONE_FATTIBILITA') && (
                                            <TabsTrigger value="form" className="gap-2">
                                                <TextAlignLeft className="w-4 h-4" />
                                                {selectedNodeDetails?.code === 'SOPRALLUOGO' ? 'Sopralluogo BPM' : 
                                                 selectedNodeDetails?.code === 'PROGETTAZIONE_FATTIBILITA' ? 'Configuratore Tecnico' : 'Form'}
                                                {selectedNodeDetails?.form_sections?.length > 0 && 
                                                 selectedNodeDetails?.code !== 'SOPRALLUOGO' && 
                                                 selectedNodeDetails?.code !== 'PROGETTAZIONE_FATTIBILITA' && (
                                                    <Badge variant="secondary" className="ml-1 text-xs">
                                                        {selectedNodeDetails.form_sections.reduce((acc, s) => acc + (s.fields?.length || 0), 0)}
                                                    </Badge>
                                                )}
                                            </TabsTrigger>
                                        )}
                                        <TabsTrigger value="documents" className="gap-2">
                                            <Folder className="w-4 h-4" />
                                            Documenti
                                        </TabsTrigger>
                                    </TabsList>
                                    
                                    {/* Form Tab */}
                                    {(selectedNodeDetails?.form_sections?.length > 0 || selectedNodeDetails?.code === 'SOPRALLUOGO' || selectedNodeDetails?.code === 'PROGETTAZIONE_FATTIBILITA') && (
                                        <TabsContent value="form" className="p-4">
                                            {loadingNodeDetails ? (
                                                <div className="flex items-center justify-center py-8">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                                </div>
                                            ) : selectedNodeDetails?.code === 'SOPRALLUOGO' ? (
                                                /* BPM Sopralluogo Step - Custom Component */
                                                <SopralluogoStep
                                                    contractId={contract.id}
                                                    nodeId={selectedNode.id}
                                                    stepCode="SOPRALLUOGO"
                                                    onComplete={() => loadContract()}
                                                    readOnly={selectedNode.status === 'completato'}
                                                />
                                            ) : selectedNodeDetails?.code === 'PROGETTAZIONE_FATTIBILITA' ? (
                                                /* Progettazione e Fattibilita - Technical Configurator */
                                                <ProgettazioneStep
                                                    contractId={contract.id}
                                                    nodeId={selectedNode.id}
                                                    stepCode="PROGETTAZIONE_FATTIBILITA"
                                                    onComplete={() => loadContract()}
                                                    readOnly={selectedNode.status === 'completato'}
                                                />
                                            ) : (
                                                /* Standard Form Renderer */
                                                <StepFormRenderer
                                                    sections={selectedNodeDetails.form_sections}
                                                    formData={selectedNodeDetails.form_data || {}}
                                                    onSave={handleSaveFormData}
                                                    saving={savingFormData}
                                                    nodeStatus={selectedNode.status}
                                                    readOnly={selectedNode.status !== 'in_corso'}
                                                />
                                            )}
                                        </TabsContent>
                                    )}

                                    <TabsContent value="documents" className="p-4">
                                        <DocumentManager
                                            contractId={contract.id}
                                            nodeId={selectedNode.id}
                                            requiredDocs={selectedNode.required_documents || selectedNode.required_docs || selectedNode.step_snapshot?.required_documents || []}
                                            optionalDocs={selectedNode.optional_documents || selectedNode.optional_docs || selectedNode.step_snapshot?.optional_documents || []}
                                            documents={contract.documents?.filter(d => 
                                                d.contract_node_id === selectedNode.id || 
                                                d.contract_node_id === String(selectedNode._id)
                                            ) || []}
                                            onRefresh={loadContract}
                                            nodeStatus={selectedNode.status}
                                            canValidate={true}
                                        />
                                    </TabsContent>
                                </Tabs>
                            </CardContent>
                        </>
                    ) : (
                        <CardContent className="p-12 text-center">
                            <p className="text-muted-foreground">Seleziona uno step per vedere i dettagli</p>
                        </CardContent>
                    )}
                </Card>
            </div>
                </TabsContent>

                {/* Dati Base Tab */}
                <TabsContent value="dati-base" className="mt-0">
                    <ContractBaseData 
                        contract={contract} 
                        onUpdate={loadContract}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
